import {ICelebrationData, IWish} from "./types/celebration-card.types";
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import LoadingSkeleton from "../../common/loading-skeleton/loading-skeleton";
import SendWishesModal from "./utils/send-wishes-modal";
import EmptyData from "../../common/empty-data/empty-data";
import {Cake} from "lucide-react";
import server from "../../../server";
import CelebrationList from "./utils/celebration-card-list";
import {webSocketService} from "../../../services/WebSocketService";
import dayjs from "dayjs";
import {Birthday, WorkAnniversary} from "../../../domains/models/event";

const CelebrationsCardView = ({celebration, loading}: ICelebrationData) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [wishes, setWishes] = useState<IWish[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<Birthday | WorkAnniversary | null>(null);
    const [loadingWishes, setLoadingWishes] = useState(true);

    const shouldShowWishButton = (startDate: string) => {
        const today = new Date();
        // This changes the time to 12:00 AM (midnight)
        // By setting the time to midnight, we are comparing only the dates, not the hours or minutes.
        today.setHours(0, 0, 0, 0);

        const [year, month, day] = startDate.split("-").map(Number);
        // We did month-1 because javascript months start from 0
        const eventDate = new Date(year, month - 1, day);

        const diffInDays = Math.floor(
            // dividing converts milliseconds into days.
            (today.getTime() - eventDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        return diffInDays >= -2 && diffInDays <= 2;
    };


    const isToday = dayjs(selectedEvent?.startDate).isSame(dayjs(), "day");

    const handleOnClick = () => {
        navigate('/highlights/celebrations')
    }

    useEffect(() => {

        if (!open || !selectedEvent) {
            return;
        }

        // CREATE
        webSocketService.subscribeToTopic(
            "wishes",
            "/topic/wishes",
            (message) => {
                const msg = JSON.parse(message);

                if (msg.eventId !== selectedEvent.id) {
                    return;
                }

                setWishes(prev => {

                    const existingWish = prev.find(
                        item => item.id === msg);

                    // UPDATE existing wish
                    if (existingWish) {
                        return prev.map(item =>
                            item.id === msg.id
                                ? {
                                    ...item,
                                    ...msg
                                }
                                : item
                        );
                    }

                    // CREATE new wish
                    return [...prev, msg];
                });
            }
        );

        webSocketService.subscribeToTopic(
            "deleted-wishes",
            "/topic/wishes/deleted",
            (message) => {
                const deletedWish = JSON.parse(message);
                setWishes(wish =>
                    wish.filter(
                        wish => wish.id !== deletedWish.wishId
                    )
                );
            }
        );

        return () => {
            webSocketService.unsubscribeTopic("wishes");
            webSocketService.unsubscribeTopic("deleted-wishes");
        };
    }, [open, selectedEvent]);

    const handleModalOpen = async (employee: Birthday | WorkAnniversary) => {
        setLoadingWishes(true);
        setSelectedEvent(employee);
        setOpen(true);
        try {
            const wishes = await server.wish.listById(employee.id);
            setWishes(wishes);
        } catch (error) {
            console.log("Error while fetching wishes", error)
        } finally {
            setLoadingWishes(false)
        }
    };

    const hasCelebrationData = celebration.length > 0;
    return (
        <>
            <div className={'dashboard-card dashboard-celebration-card'} onClick={handleOnClick}>
                <div className={'dashboard-card__header'}>
                    <span className={'dashboard-card__icon-wrapper dashboard-celebration-card__icon-wrapper'}>
                      <Cake className={'dashboard-card__icon dashboard-celebration-card__icon'}/>
                    </span>

                    <h5 className={'dashboard-card__title'}>
                        Celebrate Together
                        <p className={'dashboard-card__description'}>Birthdays and anniversaries ahead.</p>
                    </h5>
                    <span
                        className={'dashboard-card__count-pill dashboard-celebration-card__celebration-count'}>
                        {celebration.length}{' '}{'celebrations'}
                    </span>
                </div>

                <LoadingSkeleton loading={loading}>
                    {
                        hasCelebrationData ?
                            <div className={'dashboard-celebration-card__list'}>
                                {/*render first four items*/}
                                {celebration.slice(0, 4).map((item: Birthday | WorkAnniversary) => (
                                    <CelebrationList
                                        key={item.id}
                                        eventType={item.eventType}
                                        profilePhoto={item?.profilePhoto?.thumbnails?.small?.id}
                                        name={item.name}
                                        onClick={() => handleModalOpen(item)}
                                        employeeId={item.employeeId}
                                        showWishButton={shouldShowWishButton(item.startDate)}
                                        date={item.startDate}
                                    />
                                ))}
                            </div>
                            :
                            <EmptyData
                                onClick={handleOnClick}
                                icon={<Cake className={'dashboard-celebration-card__empty-icon'}/>}
                                title={"No celebrations!"}
                                subtitle={"Don't worry, there are exciting celebrations coming up soon"}
                            />
                    }
                </LoadingSkeleton>
            </div>

            <SendWishesModal
                key={selectedEvent?.id}
                isToday={isToday}
                open={open}
                setOpen={setOpen}
                event={selectedEvent}
                wishes={wishes}
                loadingWishes={loadingWishes}
                canSendWish={
                    selectedEvent
                        ? shouldShowWishButton(selectedEvent.startDate)
                        : false
                }
            />
        </>
    )
}

export default CelebrationsCardView;
